import React, { createContext, useContext, useEffect, useState, useCallback } from "react";
import { toast } from "sonner";
import api, { apiError } from "@/lib/api";

const AppContext = createContext(null);
export const useApp = () => useContext(AppContext);

const load = (k, d) => {
  try { return JSON.parse(localStorage.getItem(k)) ?? d; } catch { return d; }
};

export function AppProvider({ children }) {
  const [user, setUser] = useState(null); // null=unknown, false=guest, object=member
  const [cart, setCart] = useState(() => load("hy_cart", []));
  const [wishlist, setWishlist] = useState(() => load("hy_wishlist", []));
  const [cartOpen, setCartOpen] = useState(false);
  const [wishOpen, setWishOpen] = useState(false);
  const [searchOpen, setSearchOpen] = useState(false);
  const [quickView, setQuickView] = useState(null);
  const [checkoutOpen, setCheckoutOpen] = useState(false);

  useEffect(() => localStorage.setItem("hy_cart", JSON.stringify(cart)), [cart]);
  useEffect(() => localStorage.setItem("hy_wishlist", JSON.stringify(wishlist)), [wishlist]);

  useEffect(() => {
    api.get("/auth/me").then((r) => setUser(r.data)).catch(() => setUser(false));
  }, []);

  const login = async (email, password) => {
    const { data } = await api.post("/auth/login", { email, password });
    setUser(data.user); return data.user;
  };
  const register = async (name, email, password) => {
    const { data } = await api.post("/auth/register", { name, email, password });
    setUser(data.user); return data.user;
  };
  const logout = async () => {
    try { await api.post("/auth/logout"); } catch { /* ignore */ }
    setUser(false); toast("Signed out");
  };

  const addToCart = useCallback((product, variant, quantity = 1) => {
    if (!variant) { toast.error("Select a colour and size first"); return; }
    setCart((prev) => {
      const idx = prev.findIndex((l) => l.variant.id === variant.id);
      if (idx >= 0) {
        const copy = [...prev];
        copy[idx] = { ...copy[idx], quantity: copy[idx].quantity + quantity };
        return copy;
      }
      return [...prev, { product, variant, quantity }];
    });
    toast.success(`${product.name} added to bag`);
    setCartOpen(true);
  }, []);

  const setQty = (variantId, qty) => setCart((prev) =>
    prev.map((l) => (l.variant.id === variantId ? { ...l, quantity: Math.max(1, qty) } : l)));
  const removeFromCart = (variantId) => setCart((prev) => prev.filter((l) => l.variant.id !== variantId));
  const clearCart = () => setCart([]);

  const toggleWishlist = useCallback((product) => {
    setWishlist((prev) => {
      const exists = prev.find((p) => p.id === product.id);
      if (exists) { toast("Removed from wishlist"); return prev.filter((p) => p.id !== product.id); }
      toast.success(`${product.name} saved to wishlist`); return [...prev, product];
    });
  }, []);
  const inWishlist = (id) => wishlist.some((p) => p.id === id);

  const subtotal = cart.reduce((s, l) => s + l.product.price * l.quantity, 0);
  const cartCount = cart.reduce((s, l) => s + l.quantity, 0);

  const placeOrder = async (delivery) => {
    try {
      const items = cart.map((l) => ({ product_id: l.product.id, variant_id: l.variant.id, quantity: l.quantity }));
      const { data } = await api.post("/orders", { items, delivery });
      clearCart(); setCheckoutOpen(false); setCartOpen(false);
      return data;
    } catch (e) {
      toast.error(apiError(e.response?.data?.detail));
      throw e;
    }
  };

  const moveCartToWishlist = () => {
    setWishlist((prev) => {
      const map = new Map(prev.map((p) => [p.id, p]));
      cart.forEach((l) => map.set(l.product.id, l.product));
      return [...map.values()];
    });
    clearCart();
    setCartOpen(false);
    setWishOpen(true);
    toast.success("Your picks were saved to your wishlist");
  };

  return (
    <AppContext.Provider value={{
      user, setUser, login, register, logout,
      cart, addToCart, setQty, removeFromCart, clearCart, subtotal, cartCount,
      wishlist, toggleWishlist, inWishlist,
      cartOpen, setCartOpen, wishOpen, setWishOpen, searchOpen, setSearchOpen,
      quickView, setQuickView, checkoutOpen, setCheckoutOpen, placeOrder, moveCartToWishlist,
    }}>
      {children}
    </AppContext.Provider>
  );
}
